import { useEffect, useState } from "react";
import { BottomBtn } from "../../../../../shared"
import { Divider, Flex, Modal, message } from "antd";
import { MinusOutlined, PlusOutlined } from "@ant-design/icons";
import { ReactComponent as Close } from "../../../../../assets/images/icon/close.svg"
import { WellnessLectureInfoRow } from "../../lectureInfoRow";
import { lectureDatePeriodFormat } from "../../../../../utils";
import { createOrUpdateReservationByMemberIdAndIssuedWellnessTicketIdAndWellnessLectureIdAndReservationStatus, getBasicMemberByToken } from "../../../../../service";
import { useNavigate } from "react-router-dom";
import { useMyContext } from "../../../../../entities/context";
import { MyIssuedWellnessTicketAvaiable } from "../../myIssuedWellnessTicketAvaiable";

// 부모 grid의 두 열을 채운다. 레이블 열 폭은 가장 긴 레이블에 맞춰지고(max-content), 짧은 레이블은 양끝 정렬로 그 폭을 채운다.
const ReservationInfoRow = ({ label, value }: { label: string, value: string }) => <>
    <div className="body/b2" style={{ fontWeight: 600, textAlign: 'justify', textAlignLast: 'justify' }}>{label}</div>
    <div className="body/b2">{value}</div>
</>

// NOTE: 임시 제한 — 게스트는 최대 1명까지만 데려올 수 있도록 프론트에서 막아둔다.
// 서버에는 이 제한이 없으므로, 제한이 풀리면 이 상수와 사용처를 함께 지울 것.
const MAX_GUEST_COUNT_TEMPORARY_LIMIT = 1;
// 본인 포함 인원 기준 상한
const MAX_HEADCOUNT_TEMPORARY_LIMIT = MAX_GUEST_COUNT_TEMPORARY_LIMIT + 1;

interface IProps {
    wellnessLecture: IWellnessLectureDetailWebResponseV2
    requestWellnessLectureById: Function
    issuedWellnessTicketList: IIssuedWellnessTicketByMemberIdAndWellnessLectureIdResponseV2[]
    ticketWord: string
    purchasePath: string
}

const ReservationWithModalInWellnessLectureDetail = ({ wellnessLecture, requestWellnessLectureById, issuedWellnessTicketList, ticketWord, purchasePath }: IProps) => {
    const navigate = useNavigate();
    const [isReservationLoading, setIsReservationLoading] = useState<boolean>(false);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isReservationDisableModalOpen, setIsReservationDisableModalOpen] = useState<boolean>(false);
    const [isReservationCompleteModalOpen, setIsReservationCompleteModalOpen] = useState<boolean>();
    const [memberId, setMemberId] = useState<number>();
    const [selectedIssuedWellnessTicketId, setSelectedIssuedWellnessTicketId] = useState<number>();
    // 본인 포함 인원. 1이면 본인만, 2부터 게스트가 있다.
    const [headcount, setHeadcount] = useState<number>(1);
    const { setMyReservationHistoryTabKey } = useMyContext();

    const selectedIssuedWellnessTicket = issuedWellnessTicketList.find(issuedWellnessTicket => issuedWellnessTicket.issuedWellnessTicketId === selectedIssuedWellnessTicketId);
    // 목록의 '동반 가능' 배지와 어긋나지 않도록 잔여 횟수는 따지지 않는다. 모자라면 상한이 1이 되어 + 가 비활성으로 보인다.
    const canAddGuest = !!selectedIssuedWellnessTicket?.issuedWellnessTicketIsGuestAvailable;
    const maxHeadcount = canAddGuest
        ? Math.max(1, Math.min(MAX_HEADCOUNT_TEMPORARY_LIMIT, selectedIssuedWellnessTicket.issuedWellnessTicketRemainingCnt, wellnessLecture.remainingSeatCnt ?? selectedIssuedWellnessTicket.issuedWellnessTicketRemainingCnt))
        : 1;
    const guestCount = headcount - 1;
    const hasIssuedWellnessTicket = issuedWellnessTicketList.length > 0;
    // 보유하고 있어도 사용 불가 날짜면 선택할 수 없으므로, 예약 가능 여부는 선택 가능한 정기권 유무로 판단한다.
    const hasSelectableIssuedWellnessTicket = issuedWellnessTicketList.some(issuedWellnessTicket => issuedWellnessTicket.issuedWellnessTicketIsUsableDate);

    useEffect(() => {
        // 팝업을 새로 열 때만 초기화한다 (예약 직후 닫힘 시점에 초기화하면 완료 화면의 안내가 사라짐)
        if (isOpen) {
            setSelectedIssuedWellnessTicketId(issuedWellnessTicketList.find(issuedWellnessTicket => issuedWellnessTicket.issuedWellnessTicketIsUsableDate)?.issuedWellnessTicketId)
            // 같은 정기권이 다시 선택되면 아래 이펙트가 실행되지 않으므로 여기서도 인원을 되돌린다
            setHeadcount(1)
        }
    }, [isOpen])

    useEffect(() => {
        // 다른 정기권으로 바꾸면 동반 불가이거나 잔여가 다를 수 있으므로 인원을 되돌린다
        setHeadcount(1)
    }, [selectedIssuedWellnessTicketId])

    message.config({ top: 200 })

    const reservationButtonClick = () => {
        if (isReservationLoading) return;
        if (wellnessLecture.isFullBooking) {
            setIsReservationDisableModalOpen(true)
            return;
        }
        if (!selectedIssuedWellnessTicketId) {
            message.error(`${ticketWord}을 선택해 주세요.`);
            return;
        }
        setIsOpen(false);
        setIsReservationLoading(true);
        createOrUpdateReservationByMemberIdAndIssuedWellnessTicketIdAndWellnessLectureIdAndReservationStatus({
            issuedWellnessTicketId: selectedIssuedWellnessTicketId,
            wellnessLectureId: wellnessLecture.wellnessLectureId,
            guestCount: guestCount
        })
            .then(() => {
                setIsReservationCompleteModalOpen(true);
            })
            .catch((error) => { console.error("Error :", error); error.response.data.includes("Error YogaLecture is Passed.") && message.error("예약 할 수 없는 수업 입니다.") })
            .finally(() => setIsReservationLoading(false))
    }
    const onClickSuccessReservation = () => {
        setIsReservationCompleteModalOpen(false);
        setMyReservationHistoryTabKey(1)
        navigate(`/my/reservation-history/${memberId}`)
    }
    useEffect(() => {
        if (isReservationCompleteModalOpen === false) {
            requestWellnessLectureById()
            getBasicMemberByToken()
                .then(res => setMemberId(res.data.id))
                .catch(error => console.error('Error', error))
        }

    }, [isReservationCompleteModalOpen])

    return <>
        <BottomBtn
            onClick={() => setIsOpen(true)}
            text={isReservationLoading ? "예약 중" : "예약하기"} />
        <Modal
            // 인원 스테퍼·차감 안내가 나타나면 높이가 변하는데, centered면 그때마다 중앙 정렬이 다시 계산돼
            // 모달이 위로 밀린다. 상단을 화면 높이 기준으로 고정해 가운데 가까이 두되, 내용은 아래로만 늘어나게 한다.
            centered={false}
            style={{ top: '15vh' }}
            open={isOpen}
            styles={{ content: { padding: 16 }, footer: { margin: 0 } }}
            closeIcon={<Close width={24} height={24} stroke="var(--secondarys400)" />}
            onCancel={() => setIsOpen(false)}
            footer={null}>
            <div>
                <div className="sub-title/st3" style={{ paddingBlock: 6 }}>수업을 예약할까요?</div>
            </div>
            <div style={{ marginTop: 16, padding: 12, backgroundColor: 'var(--secondarys100)', color: 'var(--secondarys700)' }}>
                <div className="sub-title/st3 base/black" style={{ textAlign: 'center' }}>{wellnessLecture.wellnessLectureName}</div>
                <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 12 }}>
                    <ReservationInfoRow label="수업 시간" value={lectureDatePeriodFormat(wellnessLecture.wellnessLectureStartDateTime, wellnessLecture.wellnessLectureEndDateTime)} />
                    <ReservationInfoRow label="코 치" value={`${wellnessLecture.teacherName} 코치`} />
                    <ReservationInfoRow label="수업 장소" value={`${wellnessLecture.yogaCenterName} ${wellnessLecture.room}`} />
                </div>
            </div>
            {hasIssuedWellnessTicket && <div style={{ marginTop: 16 }}>
                <MyIssuedWellnessTicketAvaiable
                    title={`사용 가능 ${ticketWord}`}
                    issuedWellnessTicketList={issuedWellnessTicketList}
                    selectedId={selectedIssuedWellnessTicketId}
                    setSelectedId={setSelectedIssuedWellnessTicketId} />
            </div>}
            {!hasSelectableIssuedWellnessTicket && <div className="body/b2 secondary/s700" style={{ marginTop: hasIssuedWellnessTicket ? 8 : 16, textAlign: 'center' }}>이 수업에 사용할 수 있는 {ticketWord}이 없어요.</div>}
            {hasSelectableIssuedWellnessTicket && <div style={{ marginTop: 4, borderTop: '1px solid var(--secondarys300)' }} />}
            {canAddGuest && <>
                <Flex style={{ marginTop: 8, height: 32, justifyContent: 'space-between', alignItems: 'center' }}>
                    <div className="sub-title/st2">인원</div>
                    <Flex gap={16} style={{ alignItems: 'center' }}>
                        <MinusOutlined
                            onClick={() => setHeadcount(prev => Math.max(1, prev - 1))}
                            style={{ fontSize: 16, padding: 4, cursor: headcount > 1 ? 'pointer' : 'not-allowed', color: headcount > 1 ? 'var(--secondarys800)' : 'var(--secondarys300)' }} />
                        <div className="sub-title/st2" style={{ minWidth: 20, textAlign: 'center' }}>{headcount}</div>
                        <PlusOutlined
                            onClick={() => setHeadcount(prev => Math.min(maxHeadcount, prev + 1))}
                            style={{ fontSize: 16, padding: 4, cursor: headcount < maxHeadcount ? 'pointer' : 'not-allowed', color: headcount < maxHeadcount ? 'var(--secondarys800)' : 'var(--secondarys300)' }} />
                    </Flex>
                </Flex>
            </>}
            {headcount > 1 && <div className="body/b2 secondary/s700" style={{ marginTop: 12, textAlign: 'center' }}>선택하신 수강권에서 인원 수만큼 횟수가 차감돼요.</div>}
            {hasSelectableIssuedWellnessTicket
                ? <div className="sub-title/st3"
                    style={{ backgroundColor: 'black', color: 'white', paddingBlock: "12px", borderRadius: '5px', textAlign: 'center', marginTop: '8px', cursor: 'pointer' }}
                    onClick={() => { reservationButtonClick() }}>예약하기</div>
                : <div className="sub-title/st3"
                    style={{ backgroundColor: 'var(--primaryp500)', paddingBlock: "12px", borderRadius: '5px', textAlign: 'center', marginTop: '16px', cursor: 'pointer' }}
                    onClick={() => navigate(purchasePath)}>{ticketWord} 구매하러 가기</div>}
        </Modal>
        <Modal
            centered
            open={isReservationDisableModalOpen}
            onCancel={() => setIsReservationDisableModalOpen(false)}
            footer={null}>
            <div>
                <div className="sub-title/st3" style={{ paddingBlock: 6 }}>예약 할 수 없습니다</div>
                <div className="body/b1 secondary/s800">정원초과</div>
            </div>
            <div className="sub-title/st3"
                style={{ backgroundColor: 'black', color: 'white', padding: '15px', borderRadius: '5px', textAlign: 'center', marginTop: '15px', cursor: 'pointer' }}
                onClick={() => { setIsReservationDisableModalOpen(false) }}>닫기</div>
        </Modal>
        <Modal centered open={isReservationCompleteModalOpen} styles={{ content: { padding: "16px" }, footer: { margin: 0 } }} onCancel={() => setIsReservationCompleteModalOpen(false)} footer={false} closeIcon={false}>
            <div>
                <div className="sub-title/st3">예약을 완료했습니다</div>
            </div>
            <div style={{ marginTop: 16, padding: 12, backgroundColor: 'var(--secondarys100)' }}>
                {wellnessLecture.yogaCenterName === '선릉'
                    ? <>
                        <div style={{ textAlign: 'center' }}>
                            <div className="sub-title/st3 base/black">Member's Lounge</div>
                            <div className="body/b2 secondary/s700" style={{ marginTop: 12 }}>선릉 멤버십 라운지에서<br />파지티브호텔만의 웰니스를 경험해보세요</div>
                            <Divider style={{ margin: '12px 0' }} />
                            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px" }}>
                                <div className="sub-title/st3 base/black">이용 안내</div>
                                <div className="sub-title/st1 secondary/s700">09:00 - 21:30</div>
                            </div>
                            <div className="body/b2 secondary/s700" style={{ marginTop: 12 }}>
                                웰니스 드링크<br />웰니스 제품 체험<br />1:1 맞춤 상담
                            </div>
                        </div>
                    </>
                    : <>
                        <div className="sub-title/st3 base/black" style={{ textAlign: 'center' }}>{wellnessLecture.wellnessLectureName}</div>
                        <WellnessLectureInfoRow label="시간" value={lectureDatePeriodFormat(wellnessLecture.wellnessLectureStartDateTime, wellnessLecture.wellnessLectureEndDateTime)} style={{ marginTop: 12 }} />
                        <WellnessLectureInfoRow label="장소" value={`${wellnessLecture.yogaCenterName} ${wellnessLecture.room}`} />
                        <WellnessLectureInfoRow label="코치" value={`${wellnessLecture.teacherName} 코치`} />
                        {guestCount > 0 && <WellnessLectureInfoRow label="인원" value={`${headcount}명 (게스트 ${guestCount}명)`} />}
                    </>}

            </div>
            {guestCount > 0 && <div className="body/b2 secondary/s700" style={{ marginTop: 12, textAlign: 'center' }}>
                게스트 {guestCount}명과 함께 예약되었습니다.<br />수업 안내는 게스트에게 직접 전달해 주세요.
            </div>}
            <div className="sub-title/st3"
                style={{ backgroundColor: 'black', color: 'white', padding: "12px 20px", borderRadius: '5px', textAlign: 'center', marginTop: '16px', cursor: 'pointer' }}
                onClick={() => onClickSuccessReservation()}>예약 내역 확인하기</div>
        </Modal>
    </>
}
export { ReservationWithModalInWellnessLectureDetail }